import { ReactNode } from 'react'
import Link from 'next/link'
import Seo, { SITE_URL } from '@comp/seo'
import JsonLd from '@comp/structured-data'
import Layout from '@comp/layout'
import FinalCta from '@comp/home/final-cta'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'
import Button from '@comp/ui/button'
import { comparisonSlug } from './comparison-data'

export interface ListicleVendor {
  name: string
  tagline: string
  summary: string
  bestFor: string
  strengths: string[]
  considerations: string[]
  /** Internal link to a deeper comparison, if we have one. */
  compareHref?: string
  compareLabel?: string
}

export interface ListicleFaq {
  q: string
  a: string
}

interface ListiclePageProps {
  /** Optional gated-asset section rendered just before the final CTA. */
  leadMagnet?: React.ReactNode
  path: string
  seoTitle: string
  seoDescription: string
  heroEyebrow: string
  heroTitle: ReactNode
  heroBody: string
  updatedDate: string // e.g. 'July 2026'
  methodology: string[]
  disclosure: string
  vendors: ListicleVendor[]
  faqs: ListicleFaq[]
  listName: string
}

export default function ListiclePage({
  path,
  seoTitle,
  seoDescription,
  heroEyebrow,
  heroTitle,
  heroBody,
  updatedDate,
  methodology,
  disclosure,
  vendors,
  faqs,
  listName,
  leadMagnet,
}: ListiclePageProps) {
  const url = `${SITE_URL}${path}`
  const vendorId = (name: string) => `vendor-${comparisonSlug(name)}`
  return (
    <Layout>
      <Seo title={seoTitle} description={seoDescription} path={path} />
      <JsonLd
        id="listicle"
        data={{
          '@type': 'WebPage', '@id': `${url}#webpage`, url,
          name: seoTitle, description: seoDescription, inLanguage: 'en',
          publisher: { '@type': 'Organization', name: 'Monofor', url: SITE_URL },
          mainEntity: {
            '@type': 'ItemList', '@id': `${url}#vendors`, name: listName,
            itemListOrder: 'https://schema.org/ItemListOrderAscending',
            numberOfItems: vendors.length,
            itemListElement: vendors.map((v, i) => ({
              '@type': 'ListItem', position: i + 1, name: v.name,
              url: `${url}#${vendorId(v.name)}`,
              description: `${v.summary} Best for: ${v.bestFor} Strengths: ${v.strengths.join('; ')}. Considerations: ${v.considerations.join('; ')}.`,
            })),
          },
        }}
      />
      <JsonLd
        id="listicle-faq"
        data={{
          '@type': 'FAQPage',
          '@id': `${url}#faq`, inLanguage: 'en',
          mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: f.q,
            acceptedAnswer: { '@type': 'Answer', text: f.a },
          })),
        }}
      />
      <main className="bg-surface text-ink-primary" lang="en">
        {/* Hero */}
        <Section tone="default" density="compact" className="relative overflow-hidden">
          <div aria-hidden className="pointer-events-none absolute inset-0">
            <div className="absolute -top-32 -right-24 h-[640px] w-[640px] rounded-full bg-brand-100/60 blur-3xl" />
            <div className="absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-accent-100/50 blur-3xl" />
          </div>
          <Container size="xl" className="relative">
            <div className="max-w-3xl">
              <Eyebrow tone="brand">{heroEyebrow}</Eyebrow>
              <div className="mt-6">
                <Heading as="h1" size="display-xl">
                  {heroTitle}
                </Heading>
              </div>
              <div className="mt-8 max-w-2xl">
                <Text size="xl" tone="secondary">
                  {heroBody}
                </Text>
              </div>
              <div className="mt-6 text-body-sm text-ink-tertiary">
                Last updated: {updatedDate}
              </div>
              <nav aria-label="Vendors in this comparison" className="mt-8 flex flex-wrap gap-2">
                {vendors.map((vendor, i) => <a key={vendor.name} href={`#${vendorId(vendor.name)}`} className="rounded-full border border-line bg-surface px-4 py-2 text-body-sm text-brand-700 hover:bg-surface-subtle focus-visible:outline focus-visible:outline-2">
                  {i + 1}. {vendor.name}
                </a>)}
              </nav>
            </div>
          </Container>
        </Section>

        {/* Methodology + disclosure */}
        <Section tone="subtle" density="compact">
          <Container size="md">
            <Heading as="h2" size="display-sm">
              How we ranked this list
            </Heading>
            <ul className="mt-5 space-y-2">
              {methodology.map((m) => (
                <li key={m} className="flex items-start gap-2.5 text-body-md text-ink-secondary">
                  <span aria-hidden className="mt-1 text-accent-600">✓</span>
                  {m}
                </li>
              ))}
            </ul>
            <div className="mt-6 rounded-2xl border border-line-subtle bg-surface p-5 text-body-sm text-ink-tertiary">
              {disclosure}
            </div>
          </Container>
        </Section>

        {/* Ranked vendors */}
        <Section id="vendors" tone="default" density="default">
          <Container size="md">
            <div className="space-y-8">
              {vendors.map((vendor, i) => (
                <article
                  key={vendor.name}
                  id={vendorId(vendor.name)}
                  className={`scroll-mt-28 rounded-3xl border p-8 lg:p-10 ${
                    i === 0
                      ? 'border-brand-300 bg-surface shadow-card-hover ring-1 ring-brand-300'
                      : 'border-line-subtle bg-surface shadow-card'
                  }`}
                >
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
                    <span
                      className={`inline-flex h-10 w-10 items-center justify-center rounded-full text-body-lg font-medium ${
                        i === 0
                          ? 'bg-brand-900 text-white'
                          : 'bg-surface-muted text-ink-secondary'
                      }`}
                    >
                      {i + 1}
                    </span>
                    <h2 className="text-display-sm font-medium text-ink-primary">
                      {vendor.name}
                    </h2>
                    <span className="text-body-sm text-ink-tertiary">
                      {vendor.tagline}
                    </span>
                  </div>
                  <Text size="md" tone="secondary" className="mt-5">
                    {vendor.summary}
                  </Text>
                  <div className="mt-4 rounded-xl bg-surface-subtle px-4 py-3 text-body-sm text-ink-secondary">
                    <strong className="font-medium text-ink-primary">Best for: </strong>
                    {vendor.bestFor}
                  </div>
                  <div className="mt-6 grid gap-6 sm:grid-cols-2">
                    <div>
                      <div className="text-eyebrow font-semibold uppercase tracking-[0.12em] text-accent-700">
                        Strengths
                      </div>
                      <ul className="mt-3 space-y-2">
                        {vendor.strengths.map((s) => (
                          <li key={s} className="text-body-sm leading-relaxed text-ink-secondary">
                            {s}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <div className="text-eyebrow font-semibold uppercase tracking-[0.12em] text-ink-tertiary">
                        Considerations
                      </div>
                      <ul className="mt-3 space-y-2">
                        {vendor.considerations.map((c) => (
                          <li key={c} className="text-body-sm leading-relaxed text-ink-secondary">
                            {c}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  {vendor.compareHref && (
                    <div className="mt-6">
                      <Link
                        href={vendor.compareHref}
                        className="inline-flex items-center gap-1.5 text-body-sm font-medium text-brand-700 underline-offset-4 hover:underline"
                      >
                        {vendor.compareLabel ?? 'Read the full comparison'}
                        <span aria-hidden>→</span>
                      </Link>
                    </div>
                  )}
                  {i === 0 && (
                    <div className="mt-6 flex flex-wrap items-center gap-3">
                      <Button href="/try/now" variant="primary" size="md">
                        Start free trial →
                      </Button>
                      <Button href="/pricing" variant="outline" size="md">
                        See pricing
                      </Button>
                    </div>
                  )}
                </article>
              ))}
            </div>
            <div className="mt-8 text-body-sm text-ink-tertiary">
              Vendor descriptions are based on publicly available information
              as of {updatedDate}. All trademarks belong to their respective
              owners. Spotted something out of date?{' '}
              <a
                href="mailto:hello@monofor.com"
                className="underline underline-offset-4 hover:text-ink-secondary"
              >
                Tell us
              </a>
              .
            </div>
          </Container>
        </Section>

        {/* FAQ */}
        <Section id="faq" tone="subtle" density="default">
          <Container size="md">
            <Eyebrow tone="brand">FAQ</Eyebrow>
            <Heading as="h2" size="display-lg" className="mt-5">
              Common questions.
            </Heading>
            <dl className="mt-12 space-y-4">
              {faqs.map((faq) => (
                <div
                  key={faq.q}
                  className="rounded-2xl border border-line-subtle bg-surface p-6 lg:p-8"
                >
                  <dt className="text-body-lg font-medium text-ink-primary">{faq.q}</dt>
                  <dd className="mt-3 text-body-md text-ink-secondary">{faq.a}</dd>
                </div>
              ))}
            </dl>
          </Container>
        </Section>

        {leadMagnet}
        <FinalCta />
      </main>
    </Layout>
  )
}
